// How long the scan has run, beside its status. Every time comes from the backend: the count
// starts when a runner picks the scan up (startedAt), runs on the backend's clock (serverTime),
// and a finished scan shows the minutes saved with it.
import { Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { clockText, minutesText } from './model.ts';
import type { DiscoveryProgressResponse } from './types.ts';
import styles from './ScanTimer.module.css';

const RUNNING = new Set(['RUNNING', 'STOP_REQUESTED']);
/** Counts on from the backend's figure; re-keyed with each progress read, so it never drifts far. */
function LiveCount({ fromSeconds }: { fromSeconds: number }) {
  const [ticks, setTicks] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTicks((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = fromSeconds + ticks;
  return <time dateTime={`PT${seconds}S`}>{clockText(seconds)}</time>;
}

export function ScanTimer({
  progress,
  attemptId,
}: {
  progress: DiscoveryProgressResponse | null;
  /** The attempt the page shows; progress for another attempt is not counted. */
  attemptId?: string;
}) {
  if (!progress || (attemptId && progress.attemptId !== attemptId)) return null;
  if (RUNNING.has(progress.status) && progress.startedAt && progress.serverTime) {
    const fromSeconds = Math.max(
      0,
      Math.floor((Date.parse(progress.serverTime) - Date.parse(progress.startedAt)) / 1000),
    );
    return (
      <span className={styles.timer} data-state="running" role="timer">
        <Timer size={14} aria-hidden="true" />
        <span className={styles.srOnly}>Scan running for </span>
        <LiveCount key={progress.serverTime} fromSeconds={fromSeconds} />
      </span>
    );
  }
  if (typeof progress.durationSeconds === 'number') {
    return (
      <span className={styles.timer} data-state="finished">
        <Timer size={14} aria-hidden="true" />
        Last run{' '}
        <time dateTime={`PT${progress.durationSeconds}S`}>
          {minutesText(progress.durationSeconds)}
        </time>
      </span>
    );
  }
  return null;
}
